/**
 * Bloques compartidos del resumen de un turno de caja.
 *
 * Los usan el cierre (lo que acaba de pasar) y el historial (lo que pasó).
 * Están acá y no duplicados en cada pantalla porque son el mismo dato mirado en
 * dos momentos: si el desglose del cierre y el del historial no coinciden, el
 * recepcionista deja de creerle a los dos.
 *
 * DISEÑO — neutro a propósito. El color se reserva para el arqueo, que es lo
 * único con estado (cuadra / no cuadra). Las categorías del desglose no son
 * niveles de alerta y no llevan color propio.
 */

import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { methodLabel, money, numeric } from './cashShared';

/** Título de sección: versalitas espaciadas y una línea fina debajo. */
export const SectionTitle = ({ children, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="overline"
      sx={{ letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', lineHeight: 1.6 }}
    >
      {children}
    </Typography>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: -0.25 }}>
        {hint}
      </Typography>
    )}
    <Divider sx={{ mt: 1 }} />
  </Box>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
  hint: PropTypes.string
};

const headCell = { color: 'text.secondary', fontWeight: 600 };

/** Encabezado de columna, para no repetir el sx en cada celda. */
const Th = ({ children, align }) => (
  <TableCell align={align} sx={headCell}>
    {children}
  </TableCell>
);

Th.propTypes = { children: PropTypes.node, align: PropTypes.string };

/**
 * Desglose del turno: a qué concepto se imputó el dinero y por qué medio entró.
 */
export const TurnBreakdown = ({ summary, baseCurrency }) => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 5 }}>
    {summary?.conceptBreakdown && (
      <Box sx={{ minWidth: { md: 260 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          POR CONCEPTO
        </Typography>
        <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
          {[
            ['Habitaciones', summary.conceptBreakdown.room],
            ['Restaurante', summary.conceptBreakdown.restaurant],
            ['Cargos Sego', summary.conceptBreakdown.sego]
          ].map(([label, value]) => (
            <Stack key={label} direction="row" justifyContent="space-between" alignItems="baseline" sx={{ py: 1 }}>
              <Typography variant="body2" color={Number(value) ? 'text.primary' : 'text.disabled'}>
                {label}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={numeric}
                color={Number(value) ? 'text.primary' : 'text.disabled'}
              >
                {money(value || 0, baseCurrency)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    )}

    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        POR FORMA DE PAGO
      </Typography>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <Th>Método</Th>
              <Th align="center">Ops.</Th>
              <Th align="right">Monto recibido</Th>
              <Th align="right">En {baseCurrency}</Th>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summary?.detailedBreakdown || []).map((item, index) => (
              <TableRow key={`${item.paymentMethod}-${item.currency}-${index}`}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{methodLabel(item.paymentMethod)}</Typography>
                    {item.currency !== baseCurrency && <Chip size="small" variant="outlined" label={item.currency} />}
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ ...numeric, color: 'text.secondary' }}>
                  {item.count}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {money(item.amount, item.currency)}
                </TableCell>
                <TableCell align="right" sx={{ ...numeric, fontWeight: 600 }}>
                  {money(item.amountInBaseCurrency, baseCurrency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {(summary?.detailedBreakdown || []).length === 0 && (
        <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
          No hubo cobros en este turno.
        </Typography>
      )}
    </Box>
  </Stack>
);

TurnBreakdown.propTypes = {
  summary: PropTypes.object,
  baseCurrency: PropTypes.string
};

/** Cada cobro que entró en el turno. */
export const TurnTransactions = ({ transactions, baseCurrency }) => (
  <>
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <Th>Habitación</Th>
            <Th>Método</Th>
            <Th align="right">Monto</Th>
            <Th align="right">En {baseCurrency}</Th>
            <Th align="right">Tasa</Th>
          </TableRow>
        </TableHead>
        <TableBody>
          {(transactions || []).map((transaction, index) => (
            <TableRow key={`${transaction.reservationId}-${index}`} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600} sx={numeric}>
                  {transaction.roomNo ? `Hab. ${transaction.roomNo}` : '—'}
                </Typography>
                <Tooltip title={String(transaction.reservationId)}>
                  <Typography variant="caption" color="text.disabled">
                    {String(transaction.reservationId).slice(-6)}
                  </Typography>
                </Tooltip>
              </TableCell>

              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{methodLabel(transaction.paymentMethod)}</Typography>
                  {transaction.currency !== baseCurrency && (
                    <Chip size="small" variant="outlined" label={transaction.currency} />
                  )}
                  {transaction.isSplitPayment && transaction.paymentIndex && (
                    <Tooltip title="Esta reserva se pagó en varias partes">
                      <Chip size="small" variant="outlined" label={`Parte ${transaction.paymentIndex}`} />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>

              <TableCell align="right" sx={numeric}>
                {money(transaction.amount, transaction.currency)}
              </TableCell>
              <TableCell align="right" sx={{ ...numeric, fontWeight: 600 }}>
                {money(transaction.amountInBaseCurrency, baseCurrency)}
              </TableCell>
              <TableCell align="right" sx={{ ...numeric, color: 'text.secondary' }}>
                {transaction.currency === baseCurrency ? '—' : Number(transaction.exchangeRate).toFixed(4)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {(transactions || []).length === 0 && (
      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
        No hubo movimientos en este turno.
      </Typography>
    )}
  </>
);

TurnTransactions.propTypes = {
  transactions: PropTypes.array,
  baseCurrency: PropTypes.string
};
